import {
    ApplicationRef,
    ComponentRef,
    EnvironmentInjector,
    Injectable,
    NgZone,
    OutputRef,
    OutputRefSubscription,
    Type,
    createComponent,
    inject,
} from '@angular/core';
import { FtBroadcastRecommendedStoryModalComponent } from './ft-broadcast-recommended-story-modal.component';
import { FtBroadcastStoryModalComponent } from './ft-broadcast-story-modal.component';
import { FtBuildBroadcastRecommendationModalComponent } from './ft-build-broadcast-recommendation-modal.component';

/**
 * WS5 #10 Stage A (BroadcastModal port) — the native opener replacing the
 * AngularJS `BroadcastModal` service (account-v2 broadcastModal.service.ts):
 * same three entry points, same signatures. Where v1 `$compile`d the flow
 * template onto a fresh scope and body-appended it via PluggableElementRenderer
 * (+ `ftModal.show(<id>)`), this mounts the per-flow native host component onto
 * `document.body` (the StoryModalLauncherService recipe) — the host renders its
 * own modal shell, and its `closed` output (v1 `$ftFlow.close()` →
 * `ftModal.destroy` + `renderer.remove()`) tears the instance down.
 *
 * Per-call model like v1 (no single-instance guard): each call mounts a fresh
 * host; `closed` destroys that instance.
 */
@Injectable({ providedIn: 'root' })
export class BroadcastModalService {
    private readonly appRef = inject(ApplicationRef);
    private readonly environmentInjector = inject(EnvironmentInjector);
    private readonly zone = inject(NgZone);

    broadcastStoryFromStoryModal(propertyUuid: string, storyUuid: string): void {
        this.mount(FtBroadcastStoryModalComponent, { propertyUuid, storyUuid });
    }

    broadcastStoryFromRecommendation(propertyUuid: string, storyUuid: string, recommendation: any): void {
        this.mount(FtBroadcastRecommendedStoryModalComponent, { propertyUuid, storyUuid, recommendation });
    }

    buildBroadcastStoryRecommendation(propertyUuid: string, storyUuid: string): void {
        this.mount(FtBuildBroadcastRecommendationModalComponent, { propertyUuid, storyUuid });
    }

    private mount<T extends { closed: OutputRef<void> }>(
        component: Type<T>,
        inputs: Record<string, any>,
    ): void {
        this.zone.run(() => {
            const ref: ComponentRef<T> = createComponent(component, {
                environmentInjector: this.environmentInjector,
            });

            for (const [name, value] of Object.entries(inputs)) {
                ref.setInput(name, value);
            }

            let closeSub: OutputRefSubscription | undefined;
            const teardown = () => {
                closeSub?.unsubscribe();
                this.appRef.detachView(ref.hostView);
                ref.destroy();
                ref.location.nativeElement.remove();
            };
            closeSub = ref.instance.closed.subscribe(teardown);

            this.appRef.attachView(ref.hostView);
            document.body.appendChild(ref.location.nativeElement);
            ref.changeDetectorRef.detectChanges();
        });
    }
}
